import React, { useState, useRef } from "react";
import {
  Image,
  AlignLeft,
  Link as LinkIcon,
  Edit2,
  GripVertical,
  X,
  Check,
  ChevronUp,
  ChevronDown,
  MoreVertical,
  Trash2,
  Sparkles,
} from "lucide-react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { confirmEmailTemplate, generateText, fetchEmailTemplateList, generateEmailForSelectedTemplate } from "./action";
import Cookies from "js-cookie";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";

interface EmailLayoutProps {
  response: any;
  onConfirm?: () => void; 
}

interface EditModalState {
  isOpen: boolean;
  sectionIndex: number | null;
  sectionData: any;
}

interface DraggableItemProps {
  section: any;
  index: number;
  moveSection: (dragIndex: number, hoverIndex: number) => void;
  openEditModal: (index: number, section: any) => void;
  deleteSection: (index: number) => void;
  totalSections: number;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  isMenuOpen: boolean;
  onMenuToggle: () => void;
}

const ItemType = "SECTION";

const DraggableSection: React.FC<DraggableItemProps> = ({
  section,
  index,
  moveSection,
  openEditModal,
  deleteSection,
  totalSections,
  onMoveUp,
  onMoveDown,
  isMenuOpen,
  onMenuToggle,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop({
    accept: ItemType,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: any, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveSection(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemType,
    item: () => {
      return { index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  preview(drop(ref));

  return (
    <div
      ref={ref}
      data-handler-id={handlerId}
      className={`mb-2 relative group rounded transition-all duration-200 ${
        isDragging
          ? "opacity-40 scale-95 border border-dashed border-gray-300"
          : "border border-transparent hover:border-gray-200 hover:shadow-sm"
      }`}
    >
      {/* Drag Handle */}
      <div
        ref={drag}
        className="absolute -left-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
      >
        <div className="p-0.5 bg-gray-200 text-gray-600 rounded hover:bg-gray-300 shadow-sm">
          <GripVertical className="w-3 h-3" />
        </div>
      </div>

      {/* Mobile Arrow Controls */}
      <div className="absolute -left-8 top-1/2 -translate-y-1/2 md:hidden flex flex-col gap-0.5">
        <button
          onClick={() => onMoveUp(index)}
          disabled={index === 0}
          className={`p-0.5 rounded shadow-sm ${
            index === 0
              ? "bg-gray-200 text-gray-300 cursor-not-allowed"
              : "bg-gray-200 text-gray-600 hover:bg-gray-300"
          }`}
        >
          <ChevronUp className="w-3 h-3" />
        </button>
        <button
          onClick={() => onMoveDown(index)}
          disabled={index === totalSections - 1}
          className={`p-0.5 rounded shadow-sm ${
            index === totalSections - 1
              ? "bg-gray-200 text-gray-300 cursor-not-allowed"
              : "bg-gray-200 text-gray-600 hover:bg-gray-300"
          }`}
        >
          <ChevronDown className="w-3 h-3" />
        </button>
      </div>

      {/* Menu Button */}
      <div className="absolute -right-8 top-1/2 z-[200] -translate-y-1/2 opacity-0 group-hover:opacity-100 md:opacity-100 transition-opacity">
        <button
          onClick={onMenuToggle}
          className="p-0.5 bg-gray-200 text-gray-600 rounded hover:bg-gray-300 shadow-sm relative"
        >
          <MoreVertical className="w-3 h-3" />
        </button>

        {/* Dropdown Menu */}
        {isMenuOpen && (
          <>
            {/* Backdrop to close menu */}
            <div className="fixed inset-0 z-[200]" onClick={onMenuToggle} />
            <div className="absolute right-4 top-full mt-1 bg-white border border-gray-200 rounded shadow-lg z-[201] w-32 py-1">
              <button
                onClick={() => {
                  openEditModal(index, section);
                  onMenuToggle();
                }}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm text-gray-700"
              >
                <Edit2 className="w-3 h-3" />
                Edit
              </button>
              <button
                onClick={() => {
                  if (window.confirm("Are you sure you want to delete this section?")) {
                    deleteSection(index);
                  }
                  onMenuToggle();
                }}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm text-red-600"
              >
                <Trash2 className="w-3 h-3" />
                Delete
              </button>
            </div>
          </>
        )}
      </div>

      <div className="">
        {section.type === "image" && (
          <div className="flex justify-center">
            <img
              src={section.url}
              alt={section.alt_text || ""}
              className="cursor-pointer"
              style={{
                width: section.width ? `${section.width}px` : "auto",
                height: section.height ? `${section.height}px` : "auto",
                maxWidth: "100%",
                objectFit: "cover",
              }}
            />
          </div>
        )}

        {section.type === "text" && (
          <div>
            <p style={{fontSize:"14px", color:"#333"}}>
              {section.content}
            </p>
          </div>
        )}

        {section.type === "heading" && (
          
            <h2 style={{
              fontSize: section.font_size || "20px",
              color: section.color || "#1a1a1a",
              textAlign: section.alignment || "left",
              fontWeight:'600',
            }}>
              {section.content || section.text}
            </h2>
          
        )}
        {
          section.type === "description" && (          
              <p  style={{fontSize:"14px", color:"#333"}}>
                {section.content || section.text}
              </p>           
          )
        }
        {section.type === "cta" && section.layout === "img_text_btn" && (
          <div 
            className="relative overflow-hidden"
            style={{
              backgroundColor: section.background_color || "#003366",
              backgroundImage: section.background_image ? `url(${section.background_image})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div style={{display:"flex", gap:"10px",}}>
              {/* Image on the left */}
              {section.image_url && (
                <div  style={{ maxWidth: '50%', flex:"0 0 50%" }}>
                  <img
                    src={section.image_url}
                    alt={section.alt_text || "CTA Image"}
                    className="rounded-lg w-[100vw] h-auto object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement; // cast to HTMLImageElement
                      target.src =
                        "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=1470&auto=format&fit=crop";
                    }}
                  />
                </div>
              )}
              
              {/* Text and Button on the right */}
              <div className="" style={{ maxWidth: '50%', flex:"0 0 50%" }}>
                <p className="text-white text-sm mb-0" style={{
                  fontSize: section.text_size || "12px",
                }}>
                  {section.text}
                </p>
                
                {section.button_text && (
                  <div>
                    <a
                      href={section.button_url || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block mt-2 px-1 py-1 bg-white text-blue-900 font-semibold rounded hover:bg-gray-100 transition-colors"
                      style={{
                        fontSize: section.button_text_size || "12px",
                        backgroundColor: section.button_bg_color || "#ffffff",
                        color: section.button_text_color || "#003366",
                      }}
                    >
                      {section.button_text}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {section.type === "button" && (
          <div>
            <a
              target="_blank"
              rel="noreferrer"
              href={section.url || "#"}
              className="inline-flex items-center justify-center gap-2 px-2 py-2 bg-blue-600 text-white hover:bg-blue-700"
              style={{
                width: section.width ? `${section.width}px` : "auto",
                height: section.height ? `${section.height}px` : "auto",
              }}
            >
              <span>{section.text || "Action"}</span>
            </a>
          </div>
        )}

        {/* {!["image", "text", "button", "heading", "cta","description"].includes(section.type) && (
          <div className="p-3 bg-gray-50 rounded">
            <pre className="text-xs text-gray-700 overflow-auto">
              {JSON.stringify(section, null, 2)}
            </pre>
          </div>
        )} */}
      </div>
    </div>
  );
};

const NewsletterEmailPreview: React.FC<EmailLayoutProps> = ({ response ,onConfirm}) => {
  // Handle both response.layout and response.email_layout.layout
  const initialLayout = response?.email_layout?.layout || response?.layout || {};
  const [layout, setLayout] = useState(initialLayout);
  const [editModal, setEditModal] = useState<EditModalState>({
    isOpen: false,
    sectionIndex: null,
    sectionData: null,
  });
  const [headerEditOpen, setHeaderEditOpen] = useState(false);
  const [footerEditOpen, setFooterEditOpen] = useState(false);
  const [addSectionModalOpen, setAddSectionModalOpen] = useState(false);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const [textMethodModalOpen, setTextMethodModalOpen] = useState(false);
  const [aiInstructionModalOpen, setAiInstructionModalOpen] = useState(false);
  const [aiInstruction, setAiInstruction] = useState("");
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [searchParams] = useSearchParams();

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [selectedTemplatePreview, setSelectedTemplatePreview] = useState<any>(null);
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);

  if (!response || (!response.layout && !response.email_layout)) {
    return <div className="p-4 text-sm text-gray-600">No email preview available.</div>;
  }

  const header = layout.header || {};
  const body = layout.body || {};
  const footer = layout.footer || {};
  const sections = body.sections || [];

  const moveSection = (dragIndex: number, hoverIndex: number) => {
    const newSections = [...sections];
    const [draggedItem] = newSections.splice(dragIndex, 1);
    newSections.splice(hoverIndex, 0, draggedItem);

    setLayout({
      ...layout,
      body: { ...body, sections: newSections },
    });
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    moveSection(index, index - 1);
  };

  const handleMoveDown = (index: number) => {
    if (index === sections.length - 1) return;
    moveSection(index, index + 1);
  };

  const openEditModal = (index: number, section: any) => {
    setEditModal({ isOpen: true, sectionIndex: index, sectionData: { ...section } });
  };

  const closeEditModal = () => {
    setEditModal({ isOpen: false, sectionIndex: null, sectionData: null });
  };

  const saveEdit = () => {
    if (editModal.sectionIndex === null) return;

    const newSections = [...sections];
    newSections[editModal.sectionIndex] = editModal.sectionData;

    setLayout({
      ...layout,
      body: { ...body, sections: newSections },
    });
    closeEditModal();
  };

  const updateEditField = (field: string, value: any) => {
    setEditModal({
      ...editModal,
      sectionData: { ...editModal.sectionData, [field]: value },
    });
  };

  const addNewSection = (type: "text" | "image" | "button" = "text") => {
    let newSection;

    switch (type) {
      case "image":
        newSection = {
          type: "image",
          url: "https://via.placeholder.com/600x300",
          alt_text: "New image",
          width: 600,
          height: 300,
        };
        break;
      case "button":
        newSection = {
          type: "button",
          text: "Click Here",
          url: "#",
        };
        break;
      default:
        newSection = {
          type: "text",
          content: "New text section. Click edit to customize.",
        };
    }

    const newSections = [...sections, newSection];
    setLayout({
      ...layout,
      body: { ...body, sections: newSections },
    });
    setAddSectionModalOpen(false);
  };

  const handleTextSectionChoice = (method: "manual" | "ai") => {
    setTextMethodModalOpen(false);
    if (method === "manual") {
      addNewSection("text");
    } else {
      setAiInstructionModalOpen(true);
    }
  };

  const handleAIGenerate = async () => {
    if (!aiInstruction.trim()) return;

    setIsGeneratingAI(true);
    try {
      const userId = Cookies.get("user_id");
      const threadId = searchParams.get("threadId");

      if (!userId || !threadId) {
        console.error("Missing userId or threadId");
        return;
      }

      const result = await generateText(userId, threadId, aiInstruction, layout);

      if (result && result.generated_content) {
        const newSection = {
          type: "text",
          content: result.generated_content,
        };

        const newSections = [...sections, newSection];
        setLayout({
          ...layout,
          body: { ...body, sections: newSections },
        });
      }

      setAiInstructionModalOpen(false);
      setAiInstruction("");
    } catch (error) {
      console.error("Error generating AI text:", error);
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const deleteSection = (index: number) => {
    const newSections = sections.filter((_: any, idx: number) => idx !== index);
    setLayout({
      ...layout,
      body: { ...body, sections: newSections },
    });
  };

  const userId = Cookies.get("user_id");
  const threadId = `${searchParams.get("threadId")}`;

  if (!userId || !threadId) {
    return <div className="p-4 text-sm text-gray-600">User ID is not available.</div>;
  }

  const handleOpenTemplateModal = async () => {
    setTemplateModalOpen(true);
    setIsLoadingTemplates(true);
    try {
      const result = await fetchEmailTemplateList(userId);
      if (result.success && result.email_templates) {
        setTemplates(result.email_templates);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const removePromptFields = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(removePromptFields);
    } else if (obj !== null && typeof obj === "object") {
      const newObj: any = {};
      for (const key in obj) {
        if (key !== "prompt") {
          newObj[key] = removePromptFields(obj[key]);
        }
      }
      return newObj;
    }
    return obj;
  };

  const handleSelectTemplate = async (template: any) => {
    setIsGeneratingTemplate(true);
    try {
      const result = await generateEmailForSelectedTemplate(userId, threadId, template.id);
      
      if (result && result.email_layout && result.email_layout.layout) {
        const cleanedLayout: any = removePromptFields(result.email_layout.layout);
        setLayout(cleanedLayout);
    
        setTemplateModalOpen(false);
        setSelectedTemplatePreview(null);
      } else {
        console.error("No layout returned from API");
      }
    } catch (error) {
      console.error("Error generating email from template:", error);
      // Fallback to using the template layout directly if API fails
      const cleanedLayout: any = removePromptFields(template.layout);

      setLayout(cleanedLayout);
      setTemplateModalOpen(false);
      setSelectedTemplatePreview(null);
    } finally {
      setIsGeneratingTemplate(false);
    }
  };

  const handleConfirmEmailTemplate = async () => {
    setIsLoading(true);
    try {
      const result = await confirmEmailTemplate(userId, threadId, layout);
      if (result.success) {
        setIsConfirmed(true);
        setIsLoading(false);
        onConfirm?.();
      }
    } catch (error) {
      console.error("Error confirming email template:", error);
      setIsLoading(false);
    }
  };

  if (isConfirmed) {
    return (
      <div className="w-full py-6 px-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center gap-3 justify-center">
          <Check className="w-6 h-6 text-green-600" />
          <div>
            <h3 className="text-lg font-semibold text-green-900">
              Email Template Saved Successfully!
            </h3>
            <p className="text-sm text-green-700 mt-1">You can move ahead to the next step.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="h-[78vh] max-w-xl border rounded-xl border-gray-300 overflow-y-auto pn_scroller py-3" style={{ background: "#e7f1fc", padding: "0 15px" }}>
        {/* Header */}
        <div
          className="w-full flex items-center justify-between relative group"
          style={{
            backgroundColor: header.background_color || "#FFFFFF",
            fontFamily: header.font_style || "inherit",
            padding:"15px",
          }}
        >
          <div className="flex items-center gap-3">
            {header.logo_url ? (
              // <img src={header.logo_url} alt="logo" className="w-28 h-auto object-contain" />
              <Link to="https://www.broadridge.com/" target="_blank">
                <img src="https://www.broadridge.com/_Static-Assets/images/brodridgeLogo.svg" alt="logo" className="w-28 h-auto object-contain" />
              </Link>
            ) : (
              <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                <Image className="w-5 h-5 text-gray-500" />
              </div>
            )}
          </div>
          <div className="text-sm text-gray-600">&nbsp;</div>
          <button
            onClick={() => setHeaderEditOpen(true)}
            className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 bg-gray-200 text-gray-600 rounded hover:bg-gray-300 shadow-sm"
          >
            <Edit2 className="w-3 h-3" />
          </button>
        </div>

        {/* Body */}
        <div
          className="p-4 pl-10 pr-10"
          style={{
            backgroundColor: body.background_color || "#FFFFFF",
            fontFamily: body.font_style || "inherit",
          }}
        >
          {Array.isArray(sections) &&
            sections.map((section: any, idx: number) => {
              if (!section || !section.type) return null;

              return (
                <DraggableSection
                  key={idx}
                  section={section}
                  index={idx}
                  moveSection={moveSection}
                  openEditModal={openEditModal}
                  deleteSection={deleteSection}
                  totalSections={sections.length}
                  onMoveUp={handleMoveUp}
                  onMoveDown={handleMoveDown}
                  isMenuOpen={openMenuIndex === idx}
                  onMenuToggle={() => setOpenMenuIndex(openMenuIndex === idx ? null : idx)}
                />
              );
            })}

          {/* Add Section Button */}
          <button
            onClick={() => {
              setAddSectionModalOpen(true);
              setOpenMenuIndex(null);
            }}
            className="w-full py-2 mt-2 border-2 border-dashed border-gray-300 rounded text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center gap-2"
          >
            <span className="text-sm">Add Section</span>
          </button>
        </div>

        {/* Footer */}
        <div
          className="relative group"
          style={{
            fontFamily: footer.font_style || "inherit",
          }}
        >
          {/* <div
            className="text-sm text-gray-200 text-center"
            dangerouslySetInnerHTML={{ __html: footer.text || "" }}
          /> */}

          <div style={{ backgroundColor: "#f0f0f0", padding: "30px 20px" }}>
            <div style={{ maxWidth: "700px", margin: "0 auto", textAlign: "left",  }}>

              <p style={{ fontSize: "14px", lineHeight: "20px", marginBottom: "20px", color: "#3c3c3c" }}>
                Broadridge Financial Solutions (NYSE: BR) is a global technology leader with
                trusted expertise and transformative technology, helping clients and the financial
                services industry operate, innovate, and grow. We power investing, governance,
                and communications for our clients - driving operational resiliency, elevating
                business performance, and transforming investor experiences. For more information,
                please visit <a href="https://broadridge.com" style={{ color: "#2662fc" }}>Broadridge.com</a>.
              </p>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "20px 0 0" }}>
                <Link to="https://www.broadridge.com/" target="_blank">
                <img
                    src="https://www.broadridge.com/_Static-Assets/images/brodridgeLogo.svg"
                    alt="Broadridge"
                    style={{ height: "30px", objectFit: "contain" }}
                  />
                </Link>
                <div style={{ fontSize: "22px", fontWeight: "400", color: "#000c36" }}>
                  Ready for Next
                </div>
              </div>

              </div>
              </div>

              {/* Reason for receiving email */}
              <div style={{ backgroundColor: "#fff", padding: "20px 20px" }}>
              <div style={{ maxWidth: "700px", margin: "0 auto", textAlign: "left",  }}>


              <div style={{ display: "flex", justifyContent: "space-between", gap:"20px" }}>
              <div style={{ fontSize: "12px", color: "#999999", lineHeight: "16px" }}>
                You are receiving this email because <strong>anusha.kakaraparthi@broadridge.com</strong> is signed up
                to receive Broadridge communications.
              </div>

              

              {/* Company address */}
              <div style={{ fontSize: "12px", color: "#999999", lineHeight: "16px" }}>
                Delivered by Broadridge Financial Solutions, Inc., 5 Dakota Drive, Suite 300,
                Lake Success, NY 11042, USA
              </div>

              </div>

              {/* Links */}
              <div style={{ marginTop: "20px", marginBottom: "20px" }}>
                <a href="#" style={{ color: "#000c36", fontSize: "12px", marginRight: "5px" }}>Unsubscribe</a>
                /
                <a href="#" style={{ color: "#000c36", fontSize: "12px", marginLeft: "5px", marginRight: "10px" }}>Manage Preferences</a>
                /
                <a href="#" style={{ color: "#000c36", fontSize: "12px", marginLeft: "5px" }}>View as a Webpage</a>
              </div>

              {/* Disclaimer */}
              <p style={{ fontSize: "12px", color: "#999999", lineHeight: "16px", marginBottom: "20px" }}>
                This message and any attachments are intended only for the use of the addressee and
                may contain information that is privileged and confidential. If the reader of the message
                is not the intended recipient or an authorized representative of the intended recipient,
                you are hereby notified that any dissemination of this communication is strictly prohibited.
                If you have received this communication in error, please notify us immediately by e-mail
                and delete the message and any attachments from your system.
              </p>

              <div style={{ display: "flex", justifyContent: "space-between", gap:"20px" }}>
                {/* Copyright */}
                <div style={{ fontSize: "12px", color: "#777", marginBottom: "20px" }}>
                  © Copyright 2025, Broadridge Financial Solutions, Inc. <br />
                  <a href="#" style={{ color: "#1a0dab" }}>Privacy Statement</a>
                </div>

                {/* Social Icons */}
                <div style={{ display: "flex", gap: "15px", justifyContent: "center", alignItems:"center", }}>
                  <a href="#"><img src="https://i.ibb.co/21pfhBBr/br-1.jpg " style={{ width: "24px", height: "24px" }} alt="Facebook" /></a>
                  <a href="#"><img src="https://i.ibb.co/WvR1v0nG/br-2.jpg" style={{ width: "24px", height: "24px" }} alt="X" /></a>
                                   
                  <a href="https://www.linkedin.com/company/broadridge-financial-solutions/"><img src="https://i.ibb.co/35CWCWMX/br-3.jpg" style={{ width: "24px", height: "24px" }} alt="LinkedIn" /></a>
                  <a href="https://instagram.com/broadridge"><img src="https://i.ibb.co/0R8gGrZv/br-4.jpg" style={{ width: "24px", height: "24px" }} alt="Instagram" /></a> 
                </div>
              </div>

            </div>
              </div>
          

          <button
            onClick={() => setFooterEditOpen(true)}
            className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 bg-gray-200 text-gray-600 rounded hover:bg-gray-300 shadow-sm"
          >
            <Edit2 className="w-3 h-3" />
          </button>
        </div>

        {/* Edit Modal */}
        {editModal.isOpen && editModal.sectionData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Edit {editModal.sectionData.type}</h3>
                <button onClick={closeEditModal} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {editModal.sectionData.type === "image" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Image URL
                      </label>
                      <input
                        type="text"
                        value={editModal.sectionData.url || ""}
                        onChange={(e) => updateEditField("url", e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Alt Text
                      </label>
                      <input
                        type="text"
                        value={editModal.sectionData.alt_text || ""}
                        onChange={(e) => updateEditField("alt_text", e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Width (px)
                        </label>
                        <input
                          type="number"
                          value={editModal.sectionData.width || ""}
                          onChange={(e) => updateEditField("width", parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Height (px)
                        </label>
                        <input
                          type="number"
                          value={editModal.sectionData.height || ""}
                          onChange={(e) => updateEditField("height", parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </>
                )}

                {editModal.sectionData.type === "text" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                    <textarea
                      value={editModal.sectionData.content || ""}
                      onChange={(e) => updateEditField("content", e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {editModal.sectionData.type === "button" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Button Text
                      </label>
                      <input
                        type="text"
                        value={editModal.sectionData.text || ""}
                        onChange={(e) => updateEditField("text", e.target.value)}
                        className="w-full px-1 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                      <input
                        type="text"
                        value={editModal.sectionData.url || ""}
                        onChange={(e) => updateEditField("url", e.target.value)}
                        className="w-full px-2 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Width (px)
                        </label>
                        <input
                          type="number"
                          value={editModal.sectionData.width || ""}
                          onChange={(e) => updateEditField("width", parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Height (px)
                        </label>
                        <input
                          type="number"
                          value={editModal.sectionData.height || ""}
                          onChange={(e) => updateEditField("height", parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={closeEditModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEdit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header Edit Modal */}
        {headerEditOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Edit Header</h3>
                <button
                  onClick={() => setHeaderEditOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
                  <input
                    type="text"
                    value={header.logo_url || ""}
                    onChange={(e) =>
                      setLayout({ ...layout, header: { ...header, logo_url: e.target.value } })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Background Color
                  </label>
                  <input
                    type="text"
                    value={header.background_color || "#FFFFFF"}
                    onChange={(e) =>
                      setLayout({
                        ...layout,
                        header: { ...header, background_color: e.target.value },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    placeholder="#FFFFFF"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Font Style</label>
                  <input
                    type="text"
                    value={header.font_style || ""}
                    onChange={(e) =>
                      setLayout({ ...layout, header: { ...header, font_style: e.target.value } })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    placeholder="inherit"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setHeaderEditOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setHeaderEditOpen(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer Edit Modal */}
        {footerEditOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Edit Footer</h3>
                <button
                  onClick={() => setFooterEditOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Footer Text (HTML)
                  </label>
                  <textarea
                    value={footer.text || ""}
                    onChange={(e) =>
                      setLayout({ ...layout, footer: { ...footer, text: e.target.value } })
                    }
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter footer text or HTML"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Background Color
                  </label>
                  <input
                    type="text"
                    value={footer.background_color || "#F3F3F4"}
                    onChange={(e) =>
                      setLayout({
                        ...layout,
                        footer: { ...footer, background_color: e.target.value },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    placeholder="#F3F3F4"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Font Style</label>
                  <input
                    type="text"
                    value={footer.font_style || ""}
                    onChange={(e) =>
                      setLayout({ ...layout, footer: { ...footer, font_style: e.target.value } })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    placeholder="inherit"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setFooterEditOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setFooterEditOpen(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Section Modal */}
        {addSectionModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Add Section</h3>
                <button
                  onClick={() => setAddSectionModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    setAddSectionModalOpen(false);
                    setTextMethodModalOpen(true);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-3 transition-colors"
                >
                  <div className="p-2 bg-blue-50 rounded">
                    <AlignLeft className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Text</div>
                    <div className="text-sm text-gray-500">Add a text paragraph</div>
                  </div>
                </button>
                <button
                  onClick={() => addNewSection("image")}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-3 transition-colors"
                >
                  <div className="p-2 bg-green-50 rounded">
                    <Image className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Image</div>
                    <div className="text-sm text-gray-500">Add an image</div>
                  </div>
                </button>
                <button
                  onClick={() => addNewSection("button")}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-3 transition-colors"
                >
                  <div className="p-2 bg-purple-50 rounded">
                    <LinkIcon className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Button</div>
                    <div className="text-sm text-gray-500">Add a call-to-action button</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Text Method Modal */}
      {textMethodModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Text Section</h3>
              <button
                onClick={() => setTextMethodModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => handleTextSectionChoice("manual")}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-3 transition-colors"
              >
                <div className="p-2 bg-blue-50 rounded">
                  <Edit2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">Add Manually</div>
                  <div className="text-sm text-gray-500">Write your own text content</div>
                </div>
              </button>
              <button
                onClick={() => handleTextSectionChoice("ai")}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-3 transition-colors"
              >
                <div className="p-2 bg-purple-50 rounded">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">Write with AI</div>
                  <div className="text-sm text-gray-500">Generate content using AI</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Instruction Modal */}
      {aiInstructionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">AI Content Generation</h3>
              <button
                onClick={() => {
                  setAiInstructionModalOpen(false);
                  setAiInstruction("");
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  What would you like the AI to write?
                </label>
                <textarea
                  value={aiInstruction}
                  onChange={(e) => setAiInstruction(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                  placeholder="E.g., Write a compelling introduction about our new product launch..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setAiInstructionModalOpen(false);
                  setAiInstruction("");
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={isGeneratingAI}
              >
                Cancel
              </button>
              <button
                onClick={handleAIGenerate}
                disabled={!aiInstruction.trim() || isGeneratingAI}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-4 h-4" />
                {isGeneratingAI ? "Generating..." : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Selection Modal */}
      {templateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Select Email Template</h3>
              <button
                onClick={() => {
                  setTemplateModalOpen(false);
                  setSelectedTemplatePreview(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {isLoadingTemplates ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-gray-500">Loading templates...</div>
              </div>
            ) : selectedTemplatePreview ? (
              /* Template Preview */
              <div>
                <div className="mb-4">
                  <button
                    onClick={() => setSelectedTemplatePreview(null)}
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
                  >
                    <ChevronUp className="w-4 h-4 rotate-[-90deg]" />
                    Back to templates
                  </button>
                </div>
                <div className="mb-4">
                  <h4 className="text-lg font-semibold">{selectedTemplatePreview.name}</h4>
                  <p className="text-sm text-gray-600">{selectedTemplatePreview.description}</p>
                </div>
                <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 max-h-[50vh] overflow-y-auto">
                  {/* Header Preview */}
                  <div
                    className="w-full flex items-center justify-between pb-2 mb-4"
                    style={{
                      backgroundColor: selectedTemplatePreview.layout.header?.background_color || "#FFFFFF",
                      fontFamily: selectedTemplatePreview.layout.header?.font_style || "inherit",
                    }}
                  >
                    <div className="flex items-center gap-3">
                      {selectedTemplatePreview.layout.header?.logo_url ? (
                        <img
                          src={selectedTemplatePreview.layout.header.logo_url}
                          alt="logo"
                          className="w-28 h-auto object-contain"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                          <Image className="w-5 h-5 text-gray-500" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Body Preview */}
                  <div
                    className="p-4"
                    style={{
                      backgroundColor: selectedTemplatePreview.layout.body?.background_color || "#FFFFFF",
                      fontFamily: selectedTemplatePreview.layout.body?.font_style || "inherit",
                    }}
                  >
                    {selectedTemplatePreview.layout.body?.sections?.map((section: any, idx: number) => (
                      <div key={idx} className="mb-2">
                        {section.type === "image" && (
                          <div className="flex justify-center">
                            <img
                              src={section.url}
                              alt={section.alt_text || ""}
                              className="rounded-md"
                              style={{
                                width: section.width ? `${section.width}px` : "auto",
                                height: section.height ? `${section.height}px` : "auto",
                                maxWidth: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        )}
                        {section.type === "text" && (
                          <div>
                            <p className="text-gray-800 text-base leading-relaxed whitespace-pre-wrap">
                              {section.content}
                            </p>
                          </div>
                        )}
                        {section.type === "button" && (
                          <div>
                            <a
                              href={section.url || "#"}
                              className="inline-flex items-center gap-2 px-2 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                              style={{
                                width: section.width ? `${section.width}px` : "auto",
                                height: section.height ? `${section.height}px` : "auto",
                              }}
                            >
                              <span>{section.text || "Action"}</span>
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Footer Preview */}
                  <div
                    className="px-4 py-2"
                    style={{
                      backgroundColor: selectedTemplatePreview.layout.footer?.background_color || "#F3F3F4",
                      fontFamily: selectedTemplatePreview.layout.footer?.font_style || "inherit",
                    }}
                  >
                    <div
                      className="text-sm text-gray-200 text-center"
                      dangerouslySetInnerHTML={{ __html: selectedTemplatePreview.layout.footer?.text || "" }}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setSelectedTemplatePreview(null)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    disabled={isGeneratingTemplate}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSelectTemplate(selectedTemplatePreview)}
                    disabled={isGeneratingTemplate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGeneratingTemplate ? (
                      <>
                        <Sparkles className="w-4 h-4 animate-pulse" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Use This Template
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              /* Template List */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template: any) => (
                  <div
                    key={template.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => setSelectedTemplatePreview(template)}
                  >
                    <h4 className="font-semibold text-gray-900 mb-2">{template.name}</h4>
                    <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                    <div className="bg-gray-50 rounded p-2 text-xs text-gray-500">
                      <div className="flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        <span>{template.layout?.body?.sections?.length || 0} sections</span>
                      </div>
                    </div>
                  </div>
                ))}

                {templates.length === 0 && (
                  <div className="col-span-2 text-center py-12 text-gray-500">
                    No templates available
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm Layout Button */}
      <div className="mt-3 flex justify-start gap-3">
        <button
          onClick={handleOpenTemplateModal}
          className="px-4 py-1 bg-gray-200 text-gray-800 rounded-md font-semibold hover:bg-gray-300"
        >
          Select from Templates
        </button>
        <button
          onClick={handleConfirmEmailTemplate}
          className="px-4 py-1 bg-primary-900 text-white rounded-md font-semibold hover:brightness-95"
        >
          {isLoading ? "Confirming..." : "Confirm Layout"}
        </button>
      </div>
    </DndProvider>
  );
};

export default NewsletterEmailPreview;
